import React, { createContext, useContext, useState, useCallback } from 'react';

interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  onNavigateAway: (hasUnsavedChanges: boolean, callback: () => void) => boolean;
  clearPendingNavigation: () => void;
  pendingNavigation: (() => void) | null;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(undefined);

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  const onNavigateAway = useCallback((hasChanges: boolean, callback: () => void): boolean => {
    // Use the passed value directly instead of context state to avoid timing issues
    if (hasChanges) {
      setPendingNavigation(() => callback);
      return true; // Navigation blocked
    }
    // Navigation allowed, clear any pending navigation and execute callback immediately
    // Only clear if we're not already navigating (avoid clearing a legitimate pending navigation)
    setPendingNavigation((prev) => {
      if (prev) {
        // There was a pending navigation, but now we're allowing navigation
        // This shouldn't happen if hasChanges is false, but clear it anyway
        return null;
      }
      return null;
    });
    callback();
    return false;
  }, []);

  const clearPendingNavigation = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  return (
    <UnsavedChangesContext.Provider
      value={{
        hasUnsavedChanges,
        setHasUnsavedChanges,
        onNavigateAway,
        clearPendingNavigation,
        pendingNavigation,
      }}
    >
      {children}
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChanges = () => {
  const context = useContext(UnsavedChangesContext);
  if (context === undefined) {
    throw new Error('useUnsavedChanges must be used within an UnsavedChangesProvider');
  }
  return context;
};

